import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, MessageSquare, ClipboardCheck, CalendarCheck, Heart, MapPin } from "lucide-react";
import { COMPANY, IMAGES } from "../lib/constants";
import { PageHero, SectionHeading, fadeUp, ServiceAreaSection, CTABand } from "../components/common";

const values = [
  { icon: MapPin, title: "Locally Operated", desc: "Owned and run right here in Northwest Arkansas — we're your neighbors." },
  { icon: ShieldCheck, title: "Reliable & Consistent", desc: "Dependable schedules and the same high standard on every visit." },
  { icon: Sparkles, title: "Attention to Detail", desc: "Checklist-driven cleaning that catches what others miss." },
  { icon: MessageSquare, title: "Professional Communication", desc: "Clear, responsive, and easy to work with — always." },
  { icon: ClipboardCheck, title: "Customized Commercial Plans", desc: "Cleaning programs designed around your facility and budget." },
  { icon: CalendarCheck, title: "Convenient Residential Booking", desc: "Book, pay, and manage your home cleaning entirely online." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero eyebrow="About Us"
        title="A trusted Northwest Arkansas cleaning company"
        subtitle="United Building Maintenance and Residential Cleaning Services is built on reliability, detail, and a genuine commitment to our community."
        image={IMAGES.officeInterior} />

      <section className="bg-white">
        <div className="ubm-container py-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <img src={IMAGES.commercialTeam} alt="Our cleaning team" className="rounded-md border border-gray-200 w-full h-[400px] object-cover" />
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
            <div className="eyebrow mb-3">Our story</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-charcoal-900 tracking-tight">Committed to {COMPANY.region}</h2>
            <p className="mt-4 text-charcoal-600 leading-relaxed">
              United Building Maintenance and Residential Cleaning Services was founded to bring
              dependable, professional cleaning to both businesses and homeowners across Northwest Arkansas.
              We take pride in consistent results, honest communication, and treating every property as if it were our own.
            </p>
            <p className="mt-4 text-charcoal-600 leading-relaxed">
              From custom commercial janitorial programs to convenient online residential booking,
              our goal is simple: make your space cleaner and your life easier.
            </p>
            <div className="mt-6 flex items-center gap-3 bg-[#f8f9fa] border border-gray-200 rounded-md p-5">
              <Heart className="text-primary" />
              <div>
                <div className="font-semibold text-charcoal-900">Company Contacts</div>
                <div className="text-charcoal-600 text-sm">{COMPANY.owners} &bull; <a href={COMPANY.phoneLink} className="text-primary hover:underline">{COMPANY.phoneDisplay}</a></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#f8f9fa]">
        <div className="ubm-container py-20">
          <SectionHeading center eyebrow="What sets us apart" title="Why clients choose UBM" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div key={v.title} {...fadeUp} transition={{ delay: (i % 3) * 0.06 }}
                className="bg-white border border-gray-200 rounded-md p-7 hover:border-primary transition-colors">
                <v.icon className="text-primary mb-3" size={26} />
                <h3 className="font-semibold text-charcoal-900 text-lg">{v.title}</h3>
                <p className="mt-1.5 text-sm text-charcoal-600 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ServiceAreaSection />
      <CTABand />
    </div>
  );
}
